import React, { useState, useEffect, useCallback } from 'react';
import DocumentDetails from './components/DocumentDetails';
import AddDocument from './components/AddDocument';
import SharedHeader from '../../components/shared/SharedHeader';
import ProtectedPage from '../../components/shared/ProtectedPage';
import { usePermissions } from '../../utils/usePermissions';
import Pagination from '../../components/shared/Pagination';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import EmptyState from '../../components/shared/EmptyState';
import { toast } from '../../components/shared/toast';
import { apiClient } from '../../api/apiClient';
import { GET_OWNERSHIP_URL, CREATE_OWNERSHIP_URL, UPDATE_OWNERSHIP_URL } from '../../api/ownership';
import en from '../../translations/en.json';
import { buildOwnershipExportCsv, downloadCsv } from './ownershipExport';
import './Ownership.css';

function Ownership({ onSelectSong }) {
  const { canCreate } = usePermissions();
  const canCreateOwnership = canCreate('ownership');

  const [documents, setDocuments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [summaryStats, setSummaryStats] = useState({ count100: 0, count75: 0, count50: 0, countNeutral: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      let queryParams = [`page=${currentPage}`, 'limit=20'];
      if (searchQuery.trim()) {
        queryParams.push(`search=${encodeURIComponent(searchQuery.trim())}`);
      }

      const url = `${GET_OWNERSHIP_URL}?${queryParams.join('&')}`;
      const response = await apiClient(url);

      if (response) {
        setDocuments(response.data || response.documents || []);
        setTotalPages(response.totalPages || 1);
        setTotalRecords(response.totalRecords || 0);
        if (response.summary) {
          setSummaryStats(response.summary);
        }
      }
    } catch (err) {
      console.error('Failed to fetch ownership records:', err);
      toast.error('Failed to load ownership records from backend.');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery]);

  useEffect(() => {
    fetchDocuments();
  }, [currentPage, searchQuery]);

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleSaveForm = async (formData) => {
    try {
      const docId = editingDocument ? editingDocument.id : formData.id;
      let payloadBody;

      const nameVal = (formData.document_name || formData.name || formData.documentName || '').trim();

      if (formData.file instanceof File) {
        const data = new FormData();
        data.append('document_name', nameVal);
        data.append('name', nameVal);
        data.append('documentName', nameVal);
        data.append('is_singer', formData.is_singer ? 1 : 0);
        data.append('is_lyrics', formData.is_lyrics ? 1 : 0);
        data.append('is_musician', formData.is_musician ? 1 : 0);
        data.append('is_recordlabel', formData.is_recordlabel ? 1 : 0);
        data.append('document_tag', formData.document_tag || formData.tags || '');
        data.append('tags', formData.document_tag || formData.tags || '');
        data.append('is_ownership', formData.is_ownership !== undefined ? formData.is_ownership : (formData.addToOwnership === 'Yes' ? 1 : 0));
        data.append('song_ids', JSON.stringify(formData.song_ids || []));
        data.append('songs', JSON.stringify(formData.songs || []));
        data.append('document', formData.file);
        payloadBody = data;
      } else {
        payloadBody = {
          document_name: nameVal,
          name: nameVal,
          documentName: nameVal,
          is_singer: formData.is_singer ? 1 : 0,
          is_lyrics: formData.is_lyrics ? 1 : 0,
          is_musician: formData.is_musician ? 1 : 0,
          is_recordlabel: formData.is_recordlabel ? 1 : 0,
          document_tag: formData.document_tag || formData.tags || '',
          tags: formData.document_tag || formData.tags || '',
          is_ownership: formData.is_ownership !== undefined ? formData.is_ownership : (formData.addToOwnership === 'Yes' ? 1 : 0),
          document_url: formData.document_url || '',
          song_ids: formData.song_ids || [],
          songs: formData.songs || []
        };
      }

      if (docId) {
        const response = await apiClient(UPDATE_OWNERSHIP_URL(docId), {
          method: 'PUT',
          body: payloadBody
        });
        if (response) {
          toast.success('Ownership document updated successfully');
          setEditingDocument(null);
          fetchDocuments();
        }
      } else {
        const response = await apiClient(CREATE_OWNERSHIP_URL, {
          method: 'POST',
          body: payloadBody
        });
        if (response) {
          toast.success('Ownership document created successfully');
          setIsAddingNew(false);
          fetchDocuments();
        }
      }
    } catch (err) {
      console.error('Error saving ownership document:', err);
      toast.error(err.message || 'Failed to save ownership document.');
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await new Promise((resolve) => setTimeout(resolve, 800));

      const response = await apiClient(`${GET_OWNERSHIP_URL}?page=1&limit=1000`);
      const list = response?.data || response?.documents || [];

      if (list.length > 0) {
        const csvContent = buildOwnershipExportCsv(list);
        downloadCsv(csvContent, `ownership_export_${new Date().toISOString().slice(0, 10)}.csv`);
        toast.success('Ownership export completed successfully!');
      } else {
        toast.error('No ownership data available to export.');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export ownership data.');
    } finally {
      setIsExporting(false);
    }
  };

  if (editingDocument) {
    return (
      <AddDocument 
        existingDocument={editingDocument}
        onSave={handleSaveForm}
        onCancel={() => setEditingDocument(null)}
        t={t}
      />
    );
  }

  if (selectedDocument) {
    return (
      <DocumentDetails 
        document={selectedDocument}
        onBack={() => setSelectedDocument(null)}
        onEditDocument={(doc) => {
          setSelectedDocument(null);
          setEditingDocument(doc);
        }}
        onRefresh={fetchDocuments}
        onSelectSong={onSelectSong}
        t={t}
      />
    );
  }

  if (isAddingNew) {
    return (
      <AddDocument 
        onSave={handleSaveForm}
        onCancel={() => setIsAddingNew(false)}
        t={t}
      />
    );
  }

  return (
    <ProtectedPage module="ownership">
      <div className="ownership-page-wrapper">
        {/* Global Loading Overlay for Export */}
        <LoadingOverlay isLoading={isExporting} message="Generating Ownership Export..." />

        {/* Page Title */}
        <h1 className="song-title" style={{ marginBottom: '20px' }}>{t('ownership.title')}</h1>

        {/* Summary Cards */}
      <div className="ownership-summary-cards">
        <div className="ownership-summary-card">
          <div className="card-value">{t('ownership.header100')}</div>
          <div className="card-label">{t('ownership.subTitle')}</div>
          <div className="card-stat-row">
            <span className="stat-label">{t('ownership.songCountLabel')}</span>
            <span className="stat-value">{summaryStats.songCount100 || 0}</span>
          </div>
          <div className="card-stat-row">
            <span className="stat-label">{t('ownership.documentCountLabel')}</span>
            <span className="stat-value">{summaryStats.docCount100 || 0}</span>
          </div>
        </div>

        <div className="ownership-summary-card">
          <div className="card-value">{t('ownership.header75')}</div>
          <div className="card-label">{t('ownership.subTitle')}</div>
          <div className="card-stat-row">
            <span className="stat-label">{t('ownership.songCountLabel')}</span>
            <span className="stat-value">{summaryStats.songCount75 || 0}</span>
          </div>
          <div className="card-stat-row">
            <span className="stat-label">{t('ownership.documentCountLabel')}</span>
            <span className="stat-value">{summaryStats.docCount75 || 0}</span>
          </div>
        </div>

        <div className="ownership-summary-card">
          <div className="card-value">{t('ownership.header50')}</div>
          <div className="card-label">{t('ownership.subTitle')}</div>
          <div className="card-stat-row">
            <span className="stat-label">{t('ownership.songCountLabel')}</span>
            <span className="stat-value">{summaryStats.songCount50 || 0}</span>
          </div>
          <div className="card-stat-row">
            <span className="stat-label">{t('ownership.documentCountLabel')}</span>
            <span className="stat-value">{summaryStats.docCount50 || 0}</span>
          </div>
        </div>

        <div className="ownership-summary-card">
          <div className="card-value">{t('ownership.header25') || '25%'}</div>
          <div className="card-label">{t('ownership.subTitle')}</div>
          <div className="card-stat-row">
            <span className="stat-label">{t('ownership.songCountLabel')}</span>
            <span className="stat-value">{summaryStats.songCount25 || 0}</span>
          </div>
          <div className="card-stat-row">
            <span className="stat-label">{t('ownership.documentCountLabel')}</span>
            <span className="stat-value">{summaryStats.docCount25 || 0}</span>
          </div>
        </div>

        <div className="ownership-summary-card">
          <div className="card-value">{t('ownership.headerNeutral')}</div>
          <div className="card-label">{t('ownership.subTitle')}</div>
          <div className="card-stat-row">
            <span className="stat-label">{t('ownership.songCountLabel')}</span>
            <span className="stat-value">{summaryStats.songCountNeutral || 0}</span>
          </div>
          <div className="card-stat-row">
            <span className="stat-label">{t('ownership.documentCountLabel')}</span>
            <span className="stat-value">{summaryStats.docCountNeutral || 0}</span>
          </div>
        </div>
      </div>

      {/* Shared Header Controls */}
      <SharedHeader
        title=""
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t('ownership.searchPlaceholder')}
        resultCount={totalRecords}
        resultLabel={t('ownership.resultsCount')}
        showSortFilter={true}
        filterLabel="Filter"
        onSortClick={() => {}}
        onFilterClick={() => {}}
        onExportClick={handleExport}
        onAddNewClick={canCreateOwnership ? () => setIsAddingNew(true) : null}
        addNewText={t('common.addNew')}
        canAddNew={canCreateOwnership}
      />

      {/* Documents Table */}
      <div className="song-table-container">
        <div className="song-table-header ownership-table-grid">
          <div>{t('ownership.colDocName')}</div>
          <div>{t('ownership.colAddToOwnership')}</div>
          <div>{t('ownership.colSongCount')}</div>
          <div>{t('ownership.colOwnershipFor')}</div>
        </div>

        <div className="song-table-body">
          {isLoading ? (
            <LoadingOverlay isLoading message="Loading ownership documents, please wait." />
          ) : documents.length > 0 ? (
            documents.map((doc) => (
              <div 
                key={doc.id} 
                className="song-row ownership-table-grid"
                onClick={() => setSelectedDocument(doc)}
              >
                <div className="bold-text">{doc.name || doc.document_name}</div>
                <div>{doc.addToOwnership}</div>
                <div>{doc.songCount}</div>
                <div>{doc.ownershipFor}</div>
              </div>
            ))
          ) : (
            <EmptyState title="No Ownership Documents" subtitle="No ownership documents match your search." hasSearch={!!searchQuery} />
          )}
        </div>
      </div>

      {/* Pagination Footer */}
      {!isLoading && documents.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalRecords}
          onPageChange={(page) => setCurrentPage(page)}
          t={t}
        />
      )}
      </div>
    </ProtectedPage>
  );
}

export default Ownership;
